import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import {
  FaMusic,
  FaBroadcastTower,
  FaInbox,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaRupeeSign,
  FaHashtag,
  FaSearch,
  FaStar,
} from "react-icons/fa";
import { db } from "../firebase";

const COLORS = {
  text: "#0f172a",
  subtext: "#64748b",
  border: "#e2e8f0",
  card: "#ffffff",
  page: "#f5f6f8",
  primary: "#f20c53",
  secondary: "#fb6c33",
};

export default function SongRequest() {
  const [hasPermission, setHasPermission] = useState(false);
  const [tab, setTab] = useState("live");
  const [search, setSearch] = useState("");

  const [liveRequests, setLiveRequests] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [loadingLive, setLoadingLive] = useState(true);
  const [loadingReceived, setLoadingReceived] = useState(true);

  useEffect(() => {
    const raw = localStorage.getItem("employee");
    try {
      const employee = raw ? JSON.parse(raw) : null;
      const role = String(employee?.role || "").toLowerCase();
      const permissions = Array.isArray(employee?.permissions) ? employee.permissions : [];
      setHasPermission(role === "super_admin" || permissions.includes("view_song_request"));
    } catch {
      setHasPermission(false);
    }
  }, []);

  useEffect(() => {
    if (!hasPermission) return undefined;

    const liveQ = query(
      collection(db, "live_artist_for_song_request"),
      orderBy("createdAt", "desc"),
      limit(300)
    );
    const receivedQ = query(
      collection(db, "new_song_announcement"),
      orderBy("createdAt", "desc"),
      limit(300)
    );

    const unsubLive = onSnapshot(
      liveQ,
      (snap) => {
        const list = snap.docs.map((d) => {
          const x = d.data() || {};
          return {
            id: d.id,
            stageName: x.stageName || "-",
            date: x.date || "",
            timeFrom: x.timeFrom || "",
            timeTo: x.timeTo || "",
            venueName: x.venueName || "-",
            address: x.address || "",
            city: x.city || "",
            status: x.status || "hold",
            createdAt: x.createdAt || x.updatedAt || null,
            isFeatured: Boolean(x.isFeatured),
          };
        });

        list.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        setLiveRequests(list);
        setLoadingLive(false);
      },
      (err) => {
        console.error("live_artist_for_song_request error:", err);
        setLiveRequests([]);
        setLoadingLive(false);
      }
    );

    const unsubRec = onSnapshot(
      receivedQ,
      (snap) => {
        const list = snap.docs.map((d) => {
          const x = d.data() || {};
          const spotify = x.spotify || {};
          const payment = x.payment || {};
          return {
            id: d.id,
            queueNo: x.queueNo ?? null,
            stageName: x.stageName || "-",
            trackName: spotify.track_name || x.trackName || "",
            artistName: spotify.artist_name || x.artistName || "",
            occasion: x.occasion || "",
            venueName: x.venueName || "-",
            address: x.address || "",
            date: x.date || "",
            timeFrom: x.timeFrom || "",
            timeTo: x.timeTo || "",
            status: x.status || "hold",
            createdAt: x.createdAt || x.updatedAt || null,
            note: x.note || x.announcement_text || "",
            bid: x.bid ?? null,
            grandTotal: payment.grand_total ?? x.grandTotal ?? null,
            currency: payment.currency || x.currency || "INR",
            isFeatured: Boolean(x.isFeatured),
          };
        });

        list.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        setReceivedRequests(list);
        setLoadingReceived(false);
      },
      (err) => {
        console.error("new_song_announcement error:", err);
        setReceivedRequests([]);
        setLoadingReceived(false);
      }
    );

    return () => {
      unsubLive();
      unsubRec();
    };
  }, [hasPermission]);

  async function handleToggleFeatured(kind, docId, isFeatured) {
    try {
      const colName = kind === "live" ? "live_artist_for_song_request" : "new_song_announcement";
      await updateDoc(doc(db, colName, docId), { isFeatured });
    } catch (err) {
      console.error("Failed to update isFeatured:", err);
    }
  }

  const filteredLive = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return liveRequests;
    return liveRequests.filter((r) => {
      const hay = `${r.stageName} ${r.venueName} ${r.city} ${r.address}`.toLowerCase();
      return hay.includes(q);
    });
  }, [liveRequests, search]);

  const filteredReceived = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return receivedRequests;
    return receivedRequests.filter((r) => {
      const hay = `${r.trackName} ${r.artistName} ${r.stageName} ${r.occasion} ${r.venueName} ${r.note}`.toLowerCase();
      return hay.includes(q);
    });
  }, [receivedRequests, search]);

  const kpis = useMemo(() => {
    const featuredLive = liveRequests.filter((x) => x.isFeatured).length;
    const featuredReceived = receivedRequests.filter((x) => x.isFeatured).length;
    return {
      live: liveRequests.length,
      received: receivedRequests.length,
      featured: featuredLive + featuredReceived,
    };
  }, [liveRequests, receivedRequests]);

  if (!hasPermission) {
    return (
      <div style={blurScreenStyle}>
        <div style={popupBoxStyle}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <FaExclamationTriangle size={28} color="#c62828" />
          </div>
          <h2 style={{ color: "#c62828", marginBottom: 8 }}>Access Denied</h2>
          <p style={{ margin: 0 }}>You do not have permission to view Song Requests.</p>
        </div>
      </div>
    );
  }

  const activeRows = tab === "live" ? filteredLive : filteredReceived;

  return (
    <div style={pageStyle}>
      <style>{responsiveStyle}</style>

      <div style={heroStyle}>
        <div style={heroLeft}>
          <div style={heroIcon}><FaMusic /></div>
          <div>
            <h2 style={heroTitle}>Song Request Studio</h2>
            <p style={heroSub}>
              Real-time live slots and song announcements with featured controls.
            </p>
          </div>
        </div>

        <div style={kpiWrap}>
          <MiniKpi label="Live Slots" value={kpis.live} />
          <MiniKpi label="Announcements" value={kpis.received} />
          <MiniKpi label="Featured" value={kpis.featured} />
        </div>
      </div>

      <div style={toolbar}>
        <div style={tabRow}>
          <button
            type="button"
            onClick={() => setTab("live")}
            style={{ ...tabBtn, ...(tab === "live" ? tabActive : null) }}
          >
            <FaBroadcastTower /> Live Artist for Request
          </button>
          <button
            type="button"
            onClick={() => setTab("received")}
            style={{ ...tabBtn, ...(tab === "received" ? tabActive : null) }}
          >
            <FaInbox /> Requests Received
          </button>
        </div>

        <label style={searchWrap}>
          <FaSearch color="#64748b" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === "live" ? "Search by stage, venue, city" : "Search by track, artist, stage"}
            style={searchInput}
          />
        </label>
      </div>

      {tab === "live" ? (
        <LiveTab
          loading={loadingLive}
          rows={activeRows}
          onToggleFeatured={(id, val) => handleToggleFeatured("live", id, val)}
        />
      ) : (
        <ReceivedTab
          loading={loadingReceived}
          rows={activeRows}
          onToggleFeatured={(id, val) => handleToggleFeatured("received", id, val)}
        />
      )}
    </div>
  );
}

function LiveTab({ loading, rows, onToggleFeatured }) {
  if (loading) return <div style={noteBox}>Loading live slots...</div>;
  if (!rows.length) return <div style={noteBox}>No live artist requests found.</div>;

  return (
    <div className="song-live-grid">
      {rows.map((r) => (
        <article key={r.id} style={liveCard}>
          <div style={liveHead}>
            <div>
              <div style={liveTitle}>{r.stageName}</div>
              <div style={liveMetaLine}><FaMapMarkerAlt /> {r.venueName}</div>
            </div>
            <span style={statusBadge(r.status)}>{r.status || "hold"}</span>
          </div>

          <div style={liveMetaLine}><FaCalendarAlt /> {r.date || "-"}</div>
          <div style={liveMetaLine}><FaClock /> {timeText(r.timeFrom, r.timeTo)}</div>
          <div style={liveMetaLine}><FaMapMarkerAlt /> {r.city || r.address || "-"}</div>

          <div style={featureRow}>
            <label style={featureLabel}><FaStar /> Featured</label>
            <select
              value={r.isFeatured ? "true" : "false"}
              onChange={(e) => onToggleFeatured(r.id, e.target.value === "true")}
              style={featureSelect}
            >
              <option value="false">False</option>
              <option value="true">True</option>
            </select>
          </div>

          <div style={cardTime}>Updated: {formatDate(r.createdAt)}</div>
        </article>
      ))}
    </div>
  );
}

function ReceivedTab({ loading, rows, onToggleFeatured }) {
  if (loading) return <div style={noteBox}>Loading announcements...</div>;
  if (!rows.length) return <div style={noteBox}>No song announcements found.</div>;

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {rows.map((r) => (
        <article key={r.id} style={receivedCard}>
          <div style={receivedHead}>
            <div>
              <div style={receivedTitle}>{r.trackName || "Untitled Track"}</div>
              <div style={receivedSub}>{r.artistName || "Unknown Artist"} • Stage: {r.stageName}</div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={queuePill}><FaHashtag /> {r.queueNo ?? "-"}</span>
              <span style={statusBadge(r.status)}>{r.status || "hold"}</span>
            </div>
          </div>

          <div style={receivedGrid}>
            <div style={receivedMeta}><FaCalendarAlt /> {r.date || "-"}</div>
            <div style={receivedMeta}><FaClock /> {timeText(r.timeFrom, r.timeTo)}</div>
            <div style={receivedMeta}><FaMapMarkerAlt /> {r.venueName || "-"}</div>
            <div style={receivedMeta}><FaRupeeSign /> {paymentText(r.grandTotal, r.bid, r.currency)}</div>
            <div style={receivedMeta}>Occasion: {r.occasion || "-"}</div>
            <div style={receivedMeta}>Created: {formatDate(r.createdAt)}</div>
          </div>

          {r.note ? <div style={noteText}>{r.note}</div> : null}

          <div style={featureRow}>
            <label style={featureLabel}><FaStar /> Featured</label>
            <select
              value={r.isFeatured ? "true" : "false"}
              onChange={(e) => onToggleFeatured(r.id, e.target.value === "true")}
              style={featureSelect}
            >
              <option value="false">False</option>
              <option value="true">True</option>
            </select>
          </div>
        </article>
      ))}
    </div>
  );
}

function MiniKpi({ label, value }) {
  return (
    <div style={kpiCard}>
      <div style={kpiLabel}>{label}</div>
      <div style={kpiValue}>{value}</div>
    </div>
  );
}

function toMillis(ts) {
  try {
    if (!ts) return 0;
    if (typeof ts?.toMillis === "function") return ts.toMillis();
    if (typeof ts?.toDate === "function") return ts.toDate().getTime();
    if (typeof ts?.seconds === "number") return ts.seconds * 1000;
    const d = Date.parse(String(ts));
    return Number.isFinite(d) ? d : 0;
  } catch {
    return 0;
  }
}

function formatDate(v) {
  const ms = toMillis(v);
  if (!ms) return "-";
  return new Date(ms).toLocaleString("en-IN");
}

function paymentText(grandTotal, bid, currency = "INR") {
  if (grandTotal != null && grandTotal !== "") return `${grandTotal} ${currency}`;
  if (bid != null && bid !== "") return `${bid} ${currency}`;
  return "-";
}

function timeText(from, to) {
  if (from && to) return `${from} - ${to}`;
  return from || to || "-";
}

const chipBase = {
  borderRadius: 999,
  padding: "4px 9px",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "capitalize",
};

function statusBadge(status) {
  const value = String(status || "").toLowerCase();
  if (["live", "approved", "online", "completed"].includes(value)) {
    return { ...chipBase, background: "#ecfdf5", color: "#047857", border: "1px solid #bbf7d0" };
  }
  if (["cancelled", "rejected", "failed"].includes(value)) {
    return { ...chipBase, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" };
  }
  return { ...chipBase, background: "#fff7ed", color: "#b45309", border: "1px solid #fed7aa" };
}

const responsiveStyle = `
  .song-live-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  @media (max-width: 1120px) {
    .song-live-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 760px) {
    .song-live-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const pageStyle = {
  padding: 14,
  fontFamily: "Inter, system-ui, sans-serif",
  background: COLORS.page,
  minHeight: "100%",
};

const heroStyle = {
  background: `linear-gradient(100deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  padding: "14px",
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  boxShadow: "0 16px 26px rgba(15,23,42,0.17)",
  flexWrap: "wrap",
};

const heroLeft = { display: "flex", alignItems: "center", gap: 10 };
const heroIcon = {
  width: 42,
  height: 42,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  background: "rgba(255,255,255,0.15)",
  border: "1px solid rgba(255,255,255,0.3)",
};
const heroTitle = { margin: 0, fontWeight: 900, fontSize: 23 };
const heroSub = { margin: "4px 0 0", fontSize: 13, opacity: 0.95 };

const kpiWrap = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };
const kpiCard = {
  borderRadius: 10,
  padding: "8px 10px",
  minWidth: 92,
  border: "1px solid rgba(255,255,255,0.35)",
  background: "rgba(255,255,255,0.15)",
  textAlign: "center",
};
const kpiLabel = { fontSize: 11, opacity: 0.95 };
const kpiValue = { fontSize: 20, fontWeight: 900, lineHeight: 1.1 };

const toolbar = {
  marginTop: 12,
  marginBottom: 12,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  background: "#fff",
  padding: "10px",
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  flexWrap: "wrap",
};

const tabRow = { display: "flex", gap: 8, flexWrap: "wrap" };
const tabBtn = {
  borderRadius: 999,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  padding: "7px 12px",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  fontWeight: 800,
};
const tabActive = {
  border: "1px solid transparent",
  color: "#fff",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  boxShadow: "0 10px 18px rgba(242,12,83,0.2)",
};

const searchWrap = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  minWidth: "min(360px, 100%)",
  flex: "1 1 240px",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 9,
  padding: "8px 10px",
};
const searchInput = {
  border: "none",
  outline: "none",
  width: "100%",
  fontSize: 13,
};

const liveCard = {
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  padding: "12px",
  boxShadow: "0 10px 18px rgba(15,23,42,.06)",
  display: "grid",
  gap: 7,
};
const liveHead = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 };
const liveTitle = { fontWeight: 900, fontSize: 17, lineHeight: 1.2, color: COLORS.text };
const liveMetaLine = { display: "inline-flex", alignItems: "center", gap: 6, color: COLORS.subtext, fontSize: 13 };
const cardTime = {
  marginTop: 2,
  borderTop: `1px dashed ${COLORS.border}`,
  paddingTop: 6,
  color: COLORS.subtext,
  fontSize: 12,
};

const receivedCard = {
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  padding: "12px",
  boxShadow: "0 10px 18px rgba(15,23,42,.06)",
  display: "grid",
  gap: 10,
};
const receivedHead = { display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" };
const receivedTitle = { fontWeight: 900, color: COLORS.text, fontSize: 16, lineHeight: 1.2 };
const receivedSub = { marginTop: 2, color: COLORS.subtext, fontSize: 13 };
const queuePill = {
  ...chipBase,
  background: "#eef2ff",
  color: "#3730a3",
  border: "1px solid #c7d2fe",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
};

const receivedGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 8,
};
const receivedMeta = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  padding: "7px 8px",
  color: COLORS.subtext,
  fontSize: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "#f8fafc",
};

const noteText = {
  borderRadius: 8,
  border: `1px dashed ${COLORS.border}`,
  background: "#fff7ed",
  padding: "8px",
  color: COLORS.text,
  fontSize: 13,
};

const featureRow = {
  marginTop: 2,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 8,
};
const featureLabel = { display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 800, color: COLORS.text, fontSize: 12 };
const featureSelect = {
  height: 30,
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  padding: "0 8px",
  fontSize: 12,
};

const noteBox = {
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  padding: "12px",
  color: COLORS.subtext,
};

const blurScreenStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  backdropFilter: "blur(4px)",
  color: "#333",
};

const popupBoxStyle = {
  background: "#fff",
  padding: "2rem",
  borderRadius: "10px",
  textAlign: "center",
  maxWidth: "400px",
  width: "90%",
  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
};

